/**
 * BACKGROUNDSELECTOR.JSX - Sélecteur de backgrounds
 * 
 * Composant UI pour changer les couleurs du Floor.
 * 
 * FONCTIONNALITÉS :
 * - Bouton circulaire fixe en bas à droite
 * - Tooltip au survol : "Changer le background"
 * - Menu qui s'ouvre au-dessus avec animation slide-up
 * - Vignettes avec aperçu du dégradé + nom
 * - Clic sur une vignette change le background
 * 
 * PROPS :
 * - onBackgroundChange(background) : Callback appelé quand on sélectionne un background
 * - activeId (optionnel) : ID du background actif (pour styling)
 * 
 * UTILISATION :
 * <BackgroundSelector 
 *   onBackgroundChange={(bg) => setCurrentBg(bg)} 
 *   activeId={currentBg.id}
 * />
 */

import { useState, useCallback } from 'react'
import { backgrounds } from '../config/backgrounds'
import { useEscapeKey } from '../hooks/useEscapeKey'
import '../styles/BackgroundSelector.css'

function BackgroundSelector({ onBackgroundChange, activeId = null }) {
  const [isOpen, setIsOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  // Fermeture avec animation
  const handleClose = useCallback(() => {
    setIsClosing(true)
    // Attendre la fin de l'animation avant de masquer le menu
    setTimeout(() => {
      setIsOpen(false)
      setIsClosing(false)
    }, 400) // Durée de l'animation de fermeture
  }, [])

  // Ferme le menu avec la touche Escape
  useEscapeKey(handleClose, isOpen)

  // Toggle du menu (clic sur le bouton uniquement)
  function handleToggle() {
    if (isOpen) {
      handleClose()
    } else {
      setIsOpen(true)
    }
  }

  // Sélection d'un background (sans fermer le menu)
  function handleSelect(background) {
    onBackgroundChange(background)
    // Le menu reste ouvert pour permettre de tester plusieurs backgrounds
  }

  return (
    <div className="background-selector">
      {/* Bouton principal */}
      <button
        className={`background-selector__button ${isOpen ? 'is-open' : ''}`}
        onClick={handleToggle}
        aria-label="Changer le background"
      >
        {/* Icône palette (menu fermé) */}
        {!isOpen && (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10c1.38 0 2.5-1.12 2.5-2.5 0-.61-.23-1.21-.64-1.67-.08-.09-.13-.21-.13-.33 0-.28.22-.5.5-.5H16c3.31 0 6-2.69 6-6 0-4.96-4.49-9-10-9zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 8 6.5 8s1.5.67 1.5 1.5S7.33 11 6.5 11zm3-4C8.67 7 8 6.33 8 5.5S8.67 4 9.5 4s1.5.67 1.5 1.5S10.33 7 9.5 7zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 4 14.5 4s1.5.67 1.5 1.5S15.33 7 14.5 7zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 8 17.5 8s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
          </svg>
        )}
        
        {/* Icône croix (menu ouvert) */}
        {isOpen && (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        )}

        {/* Tooltip */}
        {!isOpen && (
          <div className="background-selector__tooltip">
            Changer le background
          </div>
        )}
      </button>

      {/* Menu des backgrounds */}
      {isOpen && (
        <div className={`background-selector__menu ${isClosing ? 'is-closing' : ''}`}>
          {backgrounds.map((bg) => (
            <div
              key={bg.id}
              className={`background-selector__item ${
                activeId === bg.id ? 'is-active' : ''
              } ${isClosing ? 'is-closing' : ''}`}
              onClick={() => handleSelect(bg)}
            >
              {/* Vignette avec le dégradé */}
              <div
                className="background-selector__preview"
                style={{
                  background: `linear-gradient(to top, ${bg.gradient.bottom}, ${bg.gradient.top})`
                }}
              />
              
              {/* Nom du background */}
              <div className="background-selector__name">
                {bg.name}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default BackgroundSelector

